import { useState, useEffect, useRef } from 'react';
import { NAVY, GOLD } from '../utils/constants';
import { ChevronDown } from 'lucide-react';

// --- Time picker -----------------------------------------------------------
// Storage format is ALWAYS 24-hour "HH:MM" text (e.g. "13:30"); only the
// display + picking interface is 12-hour AM/PM. Conversion is display-only.
// Shared by the Cruises & Pricing screen (standing times) and the scheduled
// change forms (times from a date).
export const pad2 = (n) => String(n).padStart(2, '0');
export const fmt12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
};
// 15-minute grid across the day, stored as 24-hour "HH:MM".
export const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 15) TIME_OPTIONS.push(`${pad2(h)}:${pad2(m)}`);

const fieldStyle = {
  width: '100%', background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)',
  borderRadius: 8, padding: '9px 12px', fontSize: '0.88rem', color: NAVY,
  outline: 'none', boxSizing: 'border-box',
};

export function TimeField({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref    = useRef(null);
  const selRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { if (open && selRef.current) selRef.current.scrollIntoView({ block: 'center' }); }, [open]);

  // Keep an off-grid existing value selectable so nothing is ever lost.
  const options = value && !TIME_OPTIONS.includes(value)
    ? [...TIME_OPTIONS, value].sort()
    : TIME_OPTIONS;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ ...fieldStyle, fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ color: value ? NAVY : 'var(--color-ink-faint)' }}>{value ? fmt12(value) : 'Select time'}</span>
        <ChevronDown size={15} strokeWidth={2} aria-hidden="true" style={{ color: 'var(--color-ink-muted)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1px solid var(--color-border-strong)', borderRadius: 8, boxShadow: '0 8px 24px rgba(10,22,40,0.18)', maxHeight: 220, overflowY: 'auto', padding: 4 }}>
          {options.map(opt => {
            const sel = opt === value;
            return (
              <button key={opt} type="button" ref={sel ? selRef : null}
                onClick={() => { onChange(opt); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', borderRadius: 6, padding: '7px 10px', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: sel ? 700 : 500, color: sel ? '#fff' : NAVY, background: sel ? GOLD : 'transparent' }}
                onMouseEnter={e => { if (!sel) e.currentTarget.style.background = 'var(--color-canvas)'; }}
                onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                {fmt12(opt)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
